/**
 * The inspector's model: which controls a selected block shows, and how a
 * control's value maps to and from the block's props.
 *
 * Everything here is derived from the component's own manifest — there are no
 * per-component panels, which is what makes "add a component" a Twig-only change
 * (`@param name: kind label= default=` yields the control). The React layer is a
 * `switch` over `ParamKind`; the decisions worth testing live here.
 *
 * The value helpers exist because a prop is not a form field:
 *
 * - A colour is a `ThemeableColor` — `{literal}` or `{token}` — because the
 *   journey theme is injected into every email and an editor that only wrote hex
 *   would silently unbrand them. Tokens compile to `{{ theme.colors.brand }}`.
 * - Clearing a control must *remove* the prop, not blank it. `emitValue('')`
 *   returns null so an empty string is dropped from the compiled `with { … }`
 *   anyway, but `{literal: ''}` would emit `bg_color: ''` — a real, empty colour.
 * - A `spacing` prop is an object the component reads as `padding.top`; it has to
 *   collapse to nothing once every side is empty.
 */
import { BlockSpacing, ThemeableColor } from './document';
import { ManifestParam, ParamKind, isExpressionValue, unwrapExpression } from './manifest';
import { resolveParamLabel } from './labels';
import { BlockDefinition } from './registry';
import { EmailVariableGroup } from '../types';

/** Namespace `TemplateDrawer` adds to `variableGroups` for the journey theme. */
export const THEME_COLOR_NAMESPACE = 'theme.colors';

export type ControlGroupId = 'content' | 'style';

export interface InspectorControl {
  param: ManifestParam;
  /** Resolved from the component-label catalog, humanized as a fallback. */
  label: string;
}

export interface InspectorGroup {
  id: ControlGroupId;
  controls: InspectorControl[];
}

/** Kinds that carry *what the email says*, as opposed to how it looks. */
const CONTENT_KINDS = new Set<ParamKind>([
  'richtext',
  'text',
  'url',
  'image',
  'variable',
]);

/**
 * Names that mean appearance even on a content-ish kind. Anchored on word
 * boundaries so `url` doesn't match `border` and `alt` doesn't match `valign`.
 */
const STYLE_NAME =
  /(^|_)(color|colour|background|bg|border|radius|shadow|align|valign|width|height|size|weight|style|gap|padding|margin|spacing)(_|$)/i;

/**
 * Which section a control belongs in.
 *
 * A heuristic, deliberately: manifests declare a flat param list, and asking
 * component authors to also declare grouping would be one more thing to get
 * wrong for a purely visual benefit. Worst case a control is one heading away
 * from where you looked; its label is unchanged either way.
 */
export function controlGroupOf(param: ManifestParam): ControlGroupId {
  // A font and a CSS override are appearance whatever they are called — `font`
  // has no name to test (`font_family`, `headline_font`) and `custom_css` is a
  // style attribute by definition.
  if (
    param.kind === 'color' ||
    param.kind === 'spacing' ||
    param.kind === 'font' ||
    param.kind === 'css'
  ) {
    return 'style';
  }
  if (CONTENT_KINDS.has(param.kind) && !STYLE_NAME.test(param.name)) {
    return 'content';
  }
  return 'style';
}

/**
 * Params that do nothing until another param has a value, so showing them is an
 * offer the component will not honour.
 *
 * Width is the border switch: every `core/*` component draws
 * `{% if border_width %}border:{{ border_width }}px {{ border_style }} {{ border_color }}{% endif %}`,
 * so with no width there is no border for a colour or a style to describe.
 * Width itself is never gated — it is what turns the group on.
 */
const REQUIRES_PROP: Record<string, string> = {
  border_color: 'border_width',
  border_style: 'border_width',
  border_radius: 'border_width',
};

/**
 * Whether a prop carries a value the component would act on.
 *
 * `0` is not one. The gate mirrors a Twig `{% if %}`, and the border switch is
 * the case that makes the difference visible: *Border width → None* writes
 * `border_width: 0`, the component then draws no border, and leaving its colour,
 * style and radius on screen described a border that is not there — the one
 * reading of "None" that cannot be right.
 */
function hasValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '' || value === 0) {
    return false;
  }
  // A colour is `{literal}` / `{token}`; an object with neither is unset.
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(
      (nested) => nested !== null && nested !== undefined && nested !== '',
    );
  }
  return true;
}

/**
 * The controls for a block, grouped, manifest order preserved within a group.
 *
 * Compiler-injected params (`_cvn_*`) never appear: `manifest.ts` rejects them at
 * parse time, so they cannot reach a definition's `params`.
 *
 * `props` gates the dependent controls above. Omit it and every control shows —
 * which is what the callers that only ask "what can this component do?" want.
 */
export function inspectorGroups(
  definition: BlockDefinition,
  labels?: Record<string, string>,
  props?: Record<string, unknown>,
): InspectorGroup[] {
  const grouped: Record<ControlGroupId, InspectorControl[]> = {
    content: [],
    style: [],
  };

  const declared = new Set(definition.params.map((param) => param.name));

  for (const param of definition.params) {
    const requires = REQUIRES_PROP[param.name];
    // Only gate when the component actually declares the prerequisite —
    // otherwise a component with a lone `border_width` would lose its control.
    if (props && requires && declared.has(requires) && !hasValue(props[requires])) {
      continue;
    }

    grouped[controlGroupOf(param)].push({
      param,
      label: resolveParamLabel(param, labels),
    });
  }

  return (['content', 'style'] as ControlGroupId[])
    .map((id) => ({ id, controls: grouped[id] }))
    .filter((group) => group.controls.length > 0);
}

export type ColorMode = 'unset' | 'literal' | 'token' | 'expression';

export interface ColorValue {
  mode: ColorMode;
  /** Hex, for `literal`. */
  literal?: string;
  /** Theme token, for `token`. */
  token?: string;
  /** Raw Twig, for an expression we can't model as either. */
  expression?: string;
}

/**
 * Reads a stored colour prop.
 *
 * Tolerant of the two string shapes a stored document can carry besides the
 * `ThemeableColor` object: a bare hex (hand-authored, or an older document) and a
 * Twig expression. `{{ theme.colors.x }}` is recognized as a token binding so the
 * control shows *Theme → x* rather than an opaque expression.
 */
export function readColor(value: unknown): ColorValue {
  if (value === null || value === undefined || value === '') {
    return { mode: 'unset' };
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.token === 'string' && record.token) {
      return { mode: 'token', token: record.token };
    }
    if (typeof record.literal === 'string' && record.literal) {
      return { mode: 'literal', literal: record.literal };
    }
    return { mode: 'unset' };
  }

  if (typeof value !== 'string') return { mode: 'unset' };

  if (isExpressionValue(value)) {
    const inner = unwrapExpression(value);
    const token = inner.match(/^theme\.colors\.([A-Za-z0-9_]+)$/);
    return token
      ? { mode: 'token', token: token[1] }
      : { mode: 'expression', expression: value };
  }

  return { mode: 'literal', literal: value };
}

/** A literal colour, or undefined to clear the prop rather than emit `''`. */
export function writeColorLiteral(hex: string | null | undefined): ThemeableColor | undefined {
  const value = (hex ?? '').trim();
  return value ? { literal: value } : undefined;
}

export function writeColorToken(token: string): ThemeableColor {
  return { token };
}

/** Theme colour tokens offered for binding, from `GET /email-variables`. */
export function themeColorTokens(groups: EmailVariableGroup[] = []): string[] {
  return groups.find((group) => group.namespace === THEME_COLOR_NAMESPACE)?.items ?? [];
}

export const SPACING_SIDES = ['top', 'right', 'bottom', 'left'] as const;
export type SpacingSide = (typeof SPACING_SIDES)[number];

export function readSpacing(value: unknown): BlockSpacing {
  if (!value || typeof value !== 'object') return {};
  const record = value as Record<string, unknown>;
  const spacing: BlockSpacing = {};
  for (const side of SPACING_SIDES) {
    const raw = record[side];
    const parsed = typeof raw === 'number' ? raw : Number(raw);
    if (raw !== null && raw !== undefined && raw !== '' && Number.isFinite(parsed)) {
      spacing[side] = parsed;
    }
  }
  return spacing;
}

/**
 * One side of a spacing prop changed. Returns undefined once no side is set, so
 * an emptied control removes the prop instead of leaving `{}` — which
 * `emitValue` would drop anyway, but only after reporting it as unserializable.
 */
export function writeSpacing(
  current: unknown,
  side: SpacingSide,
  next: number | null | undefined,
): BlockSpacing | undefined {
  const spacing = readSpacing(current);
  if (next === null || next === undefined || !Number.isFinite(next)) {
    delete spacing[side];
  } else {
    spacing[side] = next;
  }
  return Object.keys(spacing).length > 0 ? spacing : undefined;
}

/** Text-ish controls coalesce a keystroke run into one undo step. */
const COALESCING_KINDS = new Set<ParamKind>([
  'richtext',
  'text',
  'url',
  'variable',
  // Typed character by character like any other text field. `font` is a picker,
  // so each choice is its own step.
  'css',
]);

export function coalescesHistory(param: ManifestParam): boolean {
  return COALESCING_KINDS.has(param.kind);
}

/**
 * The field part of an edit-run key, or undefined for a control whose every
 * change is its own undo step (a colour, a toggle, a select).
 *
 * Only the *field* — the block id is prefixed by whoever dispatches, which is
 * also what supplies `blockId` on the action. Deriving the id twice, in two
 * components, would let the write and its run key name different blocks on any
 * render where they disagreed, silently merging two blocks' edits into one undo
 * step.
 *
 * Field-scoped so that typing, tabbing to another field and typing again gives
 * two undo steps — the reducer treats a key change as the end of a run.
 */
export function editRunField(param: ManifestParam): string | undefined {
  return coalescesHistory(param) ? param.name : undefined;
}
